import { Component, OnDestroy } from '@angular/core';

import moment from 'moment-timezone';
import { combineLatest, of, Subscription } from 'rxjs';
import { take, tap, filter, finalize } from 'rxjs/operators';

import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { DistributionApi } from '../../../../../api/distributions/distribution.api';
import { EventsOverTimePoint } from '../../../../../Common/TargetingShared/model/statisticsReport/events.over.time.api.model';
import { DataPointModel } from '../../../../../Common/Charts/models/data-point.model';
import { DistributionQuotaInfo } from '../../../../../Common/Charts/models/distribution.quota.info';
import { GaugeChartOptionsModel } from '../../../../../Common/Charts/models/gaugeChart/gauge-chart-options.model';
import { GaugeChartModel } from '../../../../../Common/Charts/models/gaugeChart/gauge-chart.model';
import { LineChartOptionsModel } from '../../../../../Common/Charts/models/lineChart/line-chart-options.model';
import { LineChartModel } from '../../../../../Common/Charts/models/lineChart/line-chart.model';
import { LineSeriesModel } from '../../../../../Common/Charts/models/lineChart/line-series.model';
import {
    dateQueryInput24HoursOption, dateQueryInput30DaysOption, dateQueryInput3DaysOption,
    dateQueryInput7DaysOption, dateQueryInputAllOption, dateQueryInputLastMonthOption,
    dateQueryInputLastThreeMonthOption, dateQueryInputLastWeekOption, dateQueryInputThisMonthOption,
    dateQueryInputThisWeekOption, dateQueryInputTodayOption, dateQueryInputYesterdayOption, getDateQueryInputCustomOption
} from '../../../../../Common/Forms/models/date-query-input-options.model';
import { DateQueryModel } from '../../../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { BuyQuotaDialogComponent } from '../../../../../Common/TargetingShared/components/BuyQuotaDialog/buy-quota-dialog.component';
import { TargetingPreferencesService } from '../../../../../Common/TargetingShared/services/targeting-preferences.service';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { AccountModel } from '../../../../../Core/Users/models/account.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { DistributionAccountStatisticsModel } from '../../../../../Common/TargetingShared/model/statisticsReport/distribution-account-statistics.model';
import { DistributionService } from '../../../../../Common/TargetingShared/services/distribution.service';
import { DistributionReportService } from '../DistributionReport/distribution.report.service';
import { AgprMath } from '../../../../../Common/Misc/helpers/math/math-helper';
import {
    StatisticReportSourceType
} from '@Common/TargetingShared/model/statisticsReport/statistic-report-source-type.enum';
import { RelativeTimezoneDateTypeEnum } from '@api/mentions/enum/relative-timezone-date-type.enum';
import { ConfigService } from '@Core/Config/services/config.service';

interface AccountStatistic {
    header: string;
    label: string;
    hover: string;
    warning?: boolean;
    count: string;
    chart: GaugeChartModel;
}
interface OpenStatistic extends AccountStatistic {
    canExpand: boolean;
    expanded: boolean;
    additionalStatistics: AdditionalStatistic[];
}
interface AdditionalStatistic {
    label: string;
    info: string;
    count: string;
    emphasize: boolean;
}
@Component({
    selector: 'agpr-distribution-dashboard',
    templateUrl: './distribution.dashboard.component.html',
    styleUrls: ['./distribution.dashboard.component.scss'],
    providers: [{
            provide: DistributionReportService,
            useClass: DistributionReportService
        }],
    standalone: false
})
export class DistributionDashboardComponent implements OnDestroy {

    dateFilterSelected: DateQueryModel = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_30_DAYS);

    deliveryStatistic: AccountStatistic = {
        header : 'Discover.distributionDashboard.statistics.deliveryRate',
        label : 'Discover.distributionDashboard.statistics.deliveryRate.label',
        hover : 'Discover.distributionDashboard.statistics.deliveryRate.hover',
        count : '0',
        chart : null,
    };
    openStatistic: OpenStatistic = {
        header: 'Discover.distributionDashboard.statistics.openRate',
        label: 'Discover.distributionDashboard.statistics.openRate.label',
        hover: 'Discover.distributionDashboard.statistics.openRate.hover',
        count: '0',
        chart: null,
        canExpand: true,
        expanded: false,
        additionalStatistics: []
    };
    clickStatistic: AccountStatistic = {
        header : 'Discover.distributionDashboard.statistics.clickRate',
        label : 'Discover.distributionDashboard.statistics.clickRate.label',
        hover : 'Discover.distributionDashboard.statistics.clickRate.hover',
        count : '0',
        chart : null,
    };
    replyStatistic: AccountStatistic = {
        header : 'Discover.distributionDashboard.statistics.replyRate',
        label : 'Discover.distributionDashboard.statistics.replyRate.label',
        hover : 'Discover.distributionDashboard.statistics.replyRate.hover',
        count : '0',
        chart : null,
    };
    bounceStatistic: AccountStatistic = {
        header : 'Discover.distributionDashboard.statistics.bounceRate',
        label : 'Discover.distributionDashboard.statistics.bounceRate.label',
        hover : 'Discover.distributionDashboard.statistics.bounceRate.hover',
        count : '0',
        chart : null,
    };
    unsubscribeStatistic: AccountStatistic = {
        header : 'Discover.distributionDashboard.statistics.unsubscribeRate',
        label : 'Discover.distributionDashboard.statistics.unsubscribeRate.label',
        hover : 'Discover.distributionDashboard.statistics.unsubscribeRate.hover',
        count : '0',
        chart : null,
    };
    spamStatistic: AccountStatistic = {
        header : 'Discover.distributionDashboard.statistics.spamReportRate',
        label : 'Discover.distributionDashboard.statistics.spamReportRate.label',
        hover : 'Discover.distributionDashboard.statistics.spamReportRate.hover',
        count : '0',
        chart : null,
    };
    sentStatistic = {
        header : 'Discover.distributionDashboard.statistics.sent',
        recipientsLabel : 'Discover.distributionDashboard.statistics.sent.recipients',
        distributionsLabel : 'Discover.distributionDashboard.statistics.sent.distributions',
        hover : 'Discover.distributionDashboard.statistics.sent.hover',
        recipients: '0',
        distributions : '0',
    };

    statistics: AccountStatistic[] = [
        this.deliveryStatistic,
        this.openStatistic,
        this.clickStatistic,
        this.bounceStatistic,
        this.unsubscribeStatistic,
        this.spamStatistic,
    ];

    statisticsKeys = Object.keys(this.statistics);

    totalUsageChart: GaugeChartModel;
    monthlyUsageChart: GaugeChartModel;
    currentAccount: AccountModel;

    quotaOverTimeChart: LineChartModel;

    totalUsagePercentage: any; // type number | NaN

    quotaInfo: DistributionQuotaInfo;
    quotaOverTime: EventsOverTimePoint[];
    resetDate: Date;
    startDate: Date;
    startMonthIndex: number = 0;
    noRecipientsUsed: boolean = false;

    accountStatistics: DistributionAccountStatisticsModel;
    accountStatisticsLoading: boolean = true;
    enableMppEstimates: boolean = false;

    private subscriptions: Subscription[] = [];

    readonly distributionDateQueryInputTopicOptions: DropdownOptionModel[]  = [
        dateQueryInputAllOption,
        dateQueryInput24HoursOption,
        dateQueryInput3DaysOption,
        dateQueryInput7DaysOption,
        dateQueryInput30DaysOption,
        dateQueryInputTodayOption,
        dateQueryInputYesterdayOption,
        dateQueryInputThisWeekOption,
        dateQueryInputLastWeekOption,
        dateQueryInputThisMonthOption,
        dateQueryInputLastMonthOption,
        dateQueryInputLastThreeMonthOption,
        getDateQueryInputCustomOption()
    ];
    readonly messageSeverities = messageSeverityEnum;

    constructor(
        private localizationService: LocalizationService,
        private distributionApi: DistributionApi,
        private coreDialogService: CoreDialogService,
        private targetingPreferencesService: TargetingPreferencesService,
        private distributionService: DistributionService,
        private currentUserStateService: CurrentUserStateService,
        public userPreferenceService: UserPreferencesService,
        private configService: ConfigService,
    ) {
        this.subscriptions.push(
            combineLatest([
                this.currentUserStateService.getAccount().pipe(
                    tap((account) => this.currentAccount = account),
                    filter(x => !!x)
                ),
                this.targetingPreferencesService.getQuotaStartDate(),
                this.targetingPreferencesService.getQuotaExpiryDate().pipe(
                    tap(resetDate => this.resetDate = resetDate),
                    filter(x => !!x)
                ),
                this.targetingPreferencesService.distributionQuotaInfo$.pipe(
                    filter(x => !!x),
                    tap(quota => {
                        this.totalUsagePercentage = AgprMath.safeDivide(quota.usedDistributions, quota.totalDistributions) * 100;
                        this.noRecipientsUsed = quota.usedDistributions === 0;
                        this.quotaInfo = quota;
                    })
                ),
                this.distributionApi.getQuotaOverTime().pipe(
                    tap(events => this.quotaOverTime = events),
                    filter(x => !!x?.length)
                )
            ]).subscribe(([account, quotaStartDate, expiryDate, quotaInfo, events]) => {
                this.startDate = moment(quotaStartDate || this.currentAccount.startDate).startOf('month').toDate();
                this.startMonthIndex = moment(this.startDate).month();
                this.buildDateChart();
            }),
            this.userPreferenceService.getUserAppPreferences().pipe(filter(x => !!x)).subscribe(userPreferences => {
                this.enableMppEstimates = userPreferences.targetingPrefs.distributionsPrefs.enableMppEstimates;

                // is only needed incase the getUserAppPreferences somehow hangs and is slow to return, else is "ignored"
                this.changeEmphasizeFields();
            }),
            this.currentUserStateService.getAccount().subscribe(account => {
                if (this.configService.accountHasAccessToThirdPartyPitchingOnEnvironment(account.id) && !this.statistics.includes(this.replyStatistic)) {
                    this.statistics.push(this.replyStatistic);
                    this.statisticsKeys = Object.keys(this.statistics);
                }
            })
        );
        this.calculateAccountStats();
    }

    openBuyQuotaDialog() {
        this.coreDialogService.open(BuyQuotaDialogComponent, {
            inputs: {
                confirmAction: (amount) => {
                    return this.targetingPreferencesService.buyQuota(amount);
                }
            },
            outputs: {}
        });
    }

    calculateAccountStats(): void {
        this.accountStatisticsLoading = true;

        this.distributionService.getAccountStatsForDateRange(this.dateFilterSelected).pipe(
            take(1),
            finalize(() => this.accountStatisticsLoading = false)
        ).subscribe((accountStatistics: DistributionAccountStatisticsModel) => {
            this.accountStatistics = accountStatistics;

            this.sentStatistic.distributions = accountStatistics.sentDistributionsCount.toLocaleString();
            this.sentStatistic.recipients = accountStatistics.sentRecipientsCount.toLocaleString();

            this.deliveryStatistic.count = accountStatistics.deliveredCount.toLocaleString();
            this.deliveryStatistic.chart = this.buildStatChart(accountStatistics.deliveryRate);

            this.setOpenStatistic();

            this.openStatistic.additionalStatistics = [
                {
                    label: 'Discover.distribution.statistics.reliableOpens',
                    info: 'Discover.distribution.statistics.reliableOpens.info.' + StatisticReportSourceType.DISTRIBUTION,
                    count: (accountStatistics.reliableOpens).toLocaleString(),
                    emphasize: !this.enableMppEstimates
                },
                {
                    label: 'Discover.distribution.statistics.mppImpactedOpens',
                    info: 'Discover.distribution.statistics.mppImpactedOpens.info.' + StatisticReportSourceType.DISTRIBUTION,
                    count: accountStatistics.mppOpensCount.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                },
                {
                    label: 'Discover.distribution.statistics.actualOpens',
                    info: 'Discover.distribution.statistics.actualOpens.info.' + StatisticReportSourceType.DISTRIBUTION,
                    count: accountStatistics.estimatedActualOpens.toLocaleString(),
                    emphasize: this.enableMppEstimates
                }
            ];

            this.clickStatistic.count = accountStatistics.clicksCount.toLocaleString();
            this.clickStatistic.chart = this.buildStatChart(accountStatistics.uniqueClickRate);

            this.replyStatistic.count = accountStatistics.replyCount.toLocaleString();
            this.replyStatistic.chart = this.buildStatChart(accountStatistics.replyRate);

            this.bounceStatistic.count = accountStatistics.uniqueBounces.toLocaleString();
            this.bounceStatistic.chart = this.buildStatChart(accountStatistics.bounceRate);
            this.bounceStatistic.warning = accountStatistics.bounceRate > 5;

            this.unsubscribeStatistic.count = accountStatistics.unsubsCount.toLocaleString();
            this.unsubscribeStatistic.chart = this.buildStatChart(accountStatistics.unsubscribeRate);
            this.unsubscribeStatistic.warning = accountStatistics.unsubscribeRate > 2;

            this.spamStatistic.count = accountStatistics.spamCount.toLocaleString();
            this.spamStatistic.chart = this.buildStatChart(accountStatistics.spamRate);
            this.spamStatistic.warning = accountStatistics.spamRate > 1;
        });
    }

    buildStatChart(value: number): GaugeChartModel {
        const bigChartOptions: GaugeChartOptionsModel = {
            minXDegrees: -210,
            maxXDegrees: 30,
            percentageFontSize: 18,
            percentageFontWeight: '600',
            useOneColor: true,
        };

        return new GaugeChartModel([
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            xValue: value,
                        }
                    ]
                )
            }
        ], bigChartOptions);
    }

    buildDateChart(): void {
        const chartOptions: LineChartOptionsModel = {
            xValueLabel: this.localizationService.transform('Discover.distributionDashboard.chart.time'),
            yValueLabel: this.localizationService.transform('Discover.distributionDashboard.chart.recipients'),
            minXValue: 0,
            maxXValue: Math.floor(moment(this.resetDate).diff(moment(this.startDate), 'month')),
            minYValue: 0,
            maxYValue : this.quotaInfo.totalDistributions,
            strictMinMax: true,
            horizontalLines: [
                {
                    position: this.quotaInfo.totalDistributions,
                    color: 'red'
                }
            ],
            xLabelFunction: this.getCurrentMonthLabel.bind(this)
        };

        const pointsSlicedByDate: DataPointModel[] = [];
        const currentMonthIndex = Math.floor(moment().diff(moment(this.startDate), 'month'));

        // create month array from the period start to now
        for (let i = 0; i <= currentMonthIndex; i++) {
            pointsSlicedByDate.push({
                label: this.getCurrentMonthLabel(i, '', true),
                xValue : i,
                yValue : 0,
            });
        }

        for (const point of this.quotaOverTime){
            if (new Date(point.date).getTime() > this.startDate.getTime()) {
                const index = Math.floor(moment(point.date).diff(moment(this.startDate), 'month'));
                if (pointsSlicedByDate[index]) {
                    pointsSlicedByDate[index].yValue += point.count;
                }
            }
        }

        for (let i = 1; i < pointsSlicedByDate.length; i++) {
            pointsSlicedByDate[i].yValue += pointsSlicedByDate[i - 1].yValue;
        }

        const chartSeries: LineSeriesModel[] = [
            {
                label: '',
                dataProvider: of(pointsSlicedByDate),
                useProjection: true,
                projectionStrokeSize: 2,
                projectionStrokeDashArray: '8,3'
            }
        ];

        this.quotaOverTimeChart = new LineChartModel(chartSeries, chartOptions);
    }

    getCurrentMonthLabel(monthNumber: number, format: string = '', addYear: boolean = false): string {
        const monthIndex = (this.startMonthIndex + monthNumber) % 12;
        let monthShort = moment.monthsShort()[monthIndex];
        if (addYear) {
            const year = moment(this.startDate).add(monthNumber, 'month').year();
            monthShort += ' ' + year;
        }
        return monthShort;
    }

    toggleExpand(statistic: OpenStatistic) {
        if (statistic?.canExpand) {
            statistic.expanded = !statistic.expanded;
        }
    }

    setOpenStatistic() {
        if (this.accountStatistics) {
            if (this.enableMppEstimates) {
                this.openStatistic.count = this.accountStatistics.estimatedActualOpens.toLocaleString();
                this.openStatistic.chart = this.buildStatChart(this.accountStatistics.mppAdjustedUniqueOpenRate);
            } else {
                this.openStatistic.count = this.accountStatistics.opensCount.toLocaleString();
                this.openStatistic.chart = this.buildStatChart(this.accountStatistics.uniqueOpenRate);
            }
        }
    }

    changeEmphasizeFields() {
        this.setOpenStatistic();

        this.openStatistic?.additionalStatistics?.forEach(additionalStatistic => {
            additionalStatistic.emphasize = !additionalStatistic.emphasize;
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
